import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import contractService from '../../../services/contractService';
import { employeeService } from '../../../services/employeeService';

const ContractGeneratorPanel = ({ onContractGenerated }) => {
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    employeeId: '',
    contractType: 'full_time',
    startDate: '',
    endDate: '',
    baseSalary: '',
    hourlyRate: '',
    standardHours: '40',
    overtimeMultiplier: '1.5',
    position: '',
    department: ''
  });
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    try {
      const data = await employeeService?.getAll();
      setEmployees(data || []);
    } catch (err) {
      console.error('Failed to load employees:', err);
    }
  };

  const contractTemplates = [
    {
      type: 'full_time',
      label: 'Full-Time Employment',
      description: 'Permanent employment with full benefits and leave entitlements',
      clauses: [
        'Employment Act 1978 compliance',
        'Probation period (3-6 months)',
        'Annual leave (4 weeks per year)',
        'Sick leave provisions',
        'NASFUND superannuation (8.4%)',
        'Overtime at 1.5x rate',
        'Termination notice period',
        'Redundancy provisions'
      ]
    },
    {
      type: 'casual',
      label: 'Casual Employment',
      description: 'Irregular hours with hourly rate and public holiday provisions',
      clauses: [
        'Casual engagement basis',
        'Minimum wage compliance',
        'Hourly rate specification',
        'Public holiday rates (2.5x)',
        'No guaranteed hours',
        'Short notice termination',
        'Pro-rata leave loading'
      ]
    },
    {
      type: 'fixed_term',
      label: 'Fixed-Term Contract',
      description: 'Project-based employment with defined end date',
      clauses: [
        'Fixed term duration',
        'Project deliverables',
        'Pro-rata leave entitlements',
        'End-of-term procedures',
        'Renewal conditions',
        'Early termination clauses',
        'Final payment calculations'
      ]
    },
    {
      type: 'non_citizen',
      label: 'Non-Citizen Employment',
      description: 'Employment for non-citizens with work permit requirements',
      clauses: [
        'Work permit validation',
        'DLIR compliance requirements',
        'Localization commitments',
        'Training obligations',
        'Permit contingencies',
        'Repatriation provisions',
        'Tax residency status'
      ]
    }
  ];

  const selectedTemplate = contractTemplates?.find(t => t?.type === formData?.contractType);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };

  const handleEmployeeSelect = (e) => {
    const employeeId = e?.target?.value;
    const employee = employees?.find(emp => emp?.id === employeeId);
    
    setFormData(prev => ({
      ...prev,
      employeeId,
      position: employee?.position || '',
      department: employee?.department || ''
    }));
  };

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      setError('');
      setSuccess('');

      // Validation
      if (!formData?.employeeId) {
        setError('Please select an employee');
        return;
      }
      if (!formData?.startDate) {
        setError('Please specify start date');
        return;
      }
      if (formData?.contractType === 'full_time' && !formData?.baseSalary) {
        setError('Please specify base salary for full-time contract');
        return;
      }
      if (formData?.contractType === 'casual' && !formData?.hourlyRate) {
        setError('Please specify hourly rate for casual contract');
        return;
      }

      // Generate contract clauses
      const contractClauses = {
        template: selectedTemplate?.type,
        clauses: selectedTemplate?.clauses,
        generatedDate: new Date()?.toISOString(),
        pngEmploymentActCompliance: true
      };

      const contractData = {
        employeeId: formData?.employeeId,
        contractType: formData?.contractType,
        contractStatus: 'draft',
        startDate: formData?.startDate,
        endDate: formData?.endDate || null,
        baseSalary: formData?.baseSalary ? parseFloat(formData?.baseSalary) : null,
        hourlyRate: formData?.hourlyRate ? parseFloat(formData?.hourlyRate) : null,
        standardHours: parseFloat(formData?.standardHours),
        overtimeMultiplier: parseFloat(formData?.overtimeMultiplier),
        contractClauses
      };

      await contractService?.create(contractData);
      setSuccess('Contract generated successfully! Status: Draft');
      
      // Reset form
      setFormData({
        employeeId: '',
        contractType: 'full_time',
        startDate: '',
        endDate: '',
        baseSalary: '',
        hourlyRate: '',
        standardHours: '40',
        overtimeMultiplier: '1.5',
        position: '',
        department: ''
      });

      if (onContractGenerated) {
        onContractGenerated();
      }
    } catch (err) {
      setError(err?.message || 'Failed to generate contract');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">PNG Employment Contract Generator</h3>
          <p className="text-sm text-gray-600 mt-1">Generate compliant contracts aligned with Employment Act 1978</p>
        </div>
      </div>

      {/* Template Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {contractTemplates?.map((template) => (
          <button
            key={template?.type}
            onClick={() => setFormData(prev => ({ ...prev, contractType: template?.type }))}
            className={`text-left p-4 rounded-lg border-2 transition-all ${
              formData?.contractType === template?.type
                ? 'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300 bg-white'
            }`}
          >
            <h4 className="font-semibold text-gray-900 mb-1">{template?.label}</h4>
            <p className="text-sm text-gray-600">{template?.description}</p>
          </button>
        ))}
      </div>

      {/* Contract Form */}
      <div className="bg-gray-50 rounded-lg p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Employee *</label>
            <select
              name="employeeId"
              value={formData?.employeeId}
              onChange={handleEmployeeSelect}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select employee</option>
              {employees?.map((emp) => (
                <option key={emp?.id} value={emp?.id}>
                  {emp?.firstName} {emp?.lastName} - {emp?.employeeNumber}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date *</label>
            <Input
              type="date"
              name="startDate"
              value={formData?.startDate}
              onChange={handleInputChange}
            />
          </div>

          {(formData?.contractType === 'fixed_term' || formData?.contractType === 'non_citizen') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
              <Input
                type="date"
                name="endDate"
                value={formData?.endDate}
                onChange={handleInputChange}
              />
            </div>
          )}

          {(formData?.contractType === 'full_time' || formData?.contractType === 'fixed_term' || formData?.contractType === 'non_citizen') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Base Salary (PGK) *</label>
              <Input
                type="number"
                name="baseSalary"
                value={formData?.baseSalary}
                onChange={handleInputChange}
                placeholder="Annual salary in Kina"
              />
            </div>
          )}

          {formData?.contractType === 'casual' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Hourly Rate (PGK) *</label>
              <Input
                type="number"
                name="hourlyRate"
                value={formData?.hourlyRate}
                onChange={handleInputChange}
                placeholder="Rate per hour"
                step="0.01"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Standard Hours/Week</label>
            <Input
              type="number"
              name="standardHours"
              value={formData?.standardHours}
              onChange={handleInputChange}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Overtime Multiplier</label>
            <Input
              type="number"
              name="overtimeMultiplier"
              value={formData?.overtimeMultiplier}
              onChange={handleInputChange}
              step="0.1"
            />
          </div>
        </div>

        {/* Automated Clauses Preview */}
        {selectedTemplate && (
          <div className="mt-6 p-4 bg-white rounded-lg border border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Icon name="FileText" className="h-5 w-5 text-blue-600" />
              Automated Contract Clauses
            </h4>
            <ul className="space-y-2">
              {selectedTemplate?.clauses?.map((clause, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                  <Icon name="Check" className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  {clause}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 flex items-center gap-2">
          <Icon name="AlertCircle" className="h-5 w-5 flex-shrink-0" />
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700 flex items-center gap-2">
          <Icon name="CheckCircle" className="h-5 w-5 flex-shrink-0" />
          {success}
        </div>
      )}

      {/* Generate Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleGenerate}
          disabled={generating}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-medium flex items-center gap-2"
        >
          {generating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Generating...
            </>
          ) : (
            <>
              <Icon name="FileText" className="h-5 w-5" />
              Generate Contract
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default ContractGeneratorPanel;